import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import { Button, TextField, Select, MenuItem, Typography } from '@mui/material';
import ProductionQuantityLimitsOutlinedIcon from '@mui/icons-material/ProductionQuantityLimitsOutlined';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import PurchaseActivity from './PurchaseActivity';
import BuyManagerSelect from '../common/BuyManagerSelect';
import { useEditOrCreateSwarmMutation, useGetRewardTypeQuery } from '../../../../../../../../services/swarmsApi';
import { useNotification } from '../../../../../../../../context/NotificationContext';
import { createErrorMessage } from '../../../../../../../../utils';
import { useSelector } from 'react-redux';
import { selectAllBuyManagers } from '../../../../../../../../store/slices/buyManager/buyManagers';
import { REWARD_OPTIONS } from '../../../../../../../../constants/enums';

/**
 * Configuration form for editing swarm and event details
 * @param {Object} props - Component props
 * @param {Object} props.swarm - The swarm data
 * @param {Object} props.event - The event data
 * @param {Function} props.onSuccess - Callback function when save is successful
 * @returns {JSX.Element} The EditSwarmAndEventForm component
 */
export default function EditSwarmAndEventForm({ swarm, event }) {
  const { data: rewardTypes, isLoading: isLoadingRewardTypes } = useGetRewardTypeQuery();
  const buyManagers = useSelector(selectAllBuyManagers);
  const [editSwarm, { isLoading: isEditing }] = useEditOrCreateSwarmMutation();
  const { showNotification } = useNotification();

  const [localState, setLocalState] = useState({
    entityLink: null,
    alternativeEventUrl: '',
    eventLimitNumberOfTickets: 0,
    reward: 0,
    code: '',
    instructions: '',
    rewardTypeId: 0,
    buyManagerId: ''
  });

  // Styles for number inputs without arrows
  const numberInputStyles = {
    '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    '& input[type=number]': {
      '-moz-appearance': 'textfield',
    },
  };

  const formatRewardTypeName = (name) => {
    return name.replace(/([A-Z])/g, ' $1').trim();
  };

  // Load initial data
  useEffect(() => {
    if (swarm && event) {
      setLocalState(prev => ({
        ...prev,
        entityLink: event.entityLink || null,
        eventLimitNumberOfTickets: event.eventLimitNumberOfTickets || 0,
        reward: swarm.reward || 0,
        code: swarm.code || '',
        instructions: swarm.instructions || '',
        rewardTypeId: swarm.rewardTypeId || 0,
        buyManagerId: swarm.buyManagerId || '',
        //the alternative event URL is saved inside Encorehive database, that's why it's coming in swarm data
        alternativeEventUrl: swarm.alternativeEventUrl || ''
      }));
    }
  }, [swarm, event]);

  const handleInputChange = (field) => (event) => {
    setLocalState(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSaveChanges = async () => {
    try {
      const payload = {
        id: swarm.id,
        bteId: swarm.bteId,
        eventLimitNumberOfTickets: localState.eventLimitNumberOfTickets,
        reward: localState.reward,
        code: localState.code,
        instructions: localState.instructions,
        rewardTypeId: localState.rewardTypeId,
        buyManagerId: localState.buyManagerId,
        alternativeEventUrl: localState.alternativeEventUrl
      };

      // Update or create entityLink based on existing data
      if (event.entityLink) {
        payload.editedEntityLink = {
          id: event.entityLink.id,
          href: localState.entityLink.href
        };
      } else {
        if (localState.entityLink?.href) {
          payload.newEntityLink = {
            href: localState.entityLink.href
          };
        }
      }

      await editSwarm(payload).unwrap();
      showNotification('Event updated successfully', 'success');
    } catch (error) {
      console.error('Failed to update event:', error);
      showNotification(createErrorMessage(error), 'error');
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {/* Event URL - Full width */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontWeight: '600', width: 100, fontSize: '14px', whiteSpace: 'nowrap' }}>Event URL:</Typography>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          value={localState.entityLink?.href || ''}
          onChange={(e) => {
            const newValue = e.target.value;
            setLocalState(prev => ({
              ...prev,
              entityLink: {
                ...(prev.entityLink || {}),
                href: newValue
              }
            }));
          }}
        />
      </Box>

      {/* Alternative Event URL - Full width */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontWeight: '600', fontSize: '14px', whiteSpace: 'nowrap' }}>Alt Event URL:</Typography>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          value={localState.alternativeEventUrl}
          onChange={handleInputChange('alternativeEventUrl')}
        />
      </Box>

      {/* Tickets, Reward and Code row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {/* Ticket Limits group */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ProductionQuantityLimitsOutlinedIcon color="primary" size="small" />
          <Typography sx={{ fontWeight: '600', fontSize: '14px', whiteSpace: 'nowrap' }}>Ticket Limits:</Typography>
          <TextField
            type="number"
            value={localState.eventLimitNumberOfTickets}
            onChange={handleInputChange('eventLimitNumberOfTickets')}
            size="small"
            variant="outlined"
            sx={numberInputStyles}
          />
        </Box>

        {/* Reward group */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MonetizationOnOutlinedIcon color="primary" />
          <Typography sx={{ fontWeight: '600', fontSize: '14px', whiteSpace: 'nowrap' }}>Reward:</Typography>
          <Select
            value={localState.reward}
            onChange={handleInputChange('reward')}
            size="small"
            variant="outlined"
            sx={{ width: 100 }}
          >
            {REWARD_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
          <Select
            value={localState.rewardTypeId}
            onChange={handleInputChange('rewardTypeId')}
            size="small"
            variant="outlined"
            disabled={isLoadingRewardTypes}
            sx={{ width: 150 }}
          >
            {rewardTypes?.map(type => (
              <MenuItem key={type.id} value={type.id}>
                {formatRewardTypeName(type.name)}
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Code group */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VpnKeyOutlinedIcon color="primary" size="small" />
          <Typography sx={{ fontWeight: '600', fontSize: '14px', whiteSpace: 'nowrap' }}>Code:</Typography>
          <TextField
            value={localState.code}
            onChange={handleInputChange('code')}
            size="small"
            variant="outlined"
          />
        </Box>
      </Box>

      {/* Instructions - Full width */}
      <Box sx={{ display: 'flex', alignItems: 'start', gap: 1 }}>
        <AssignmentOutlinedIcon color="primary" size="small" />
        <Typography sx={{ fontWeight: '600', fontSize: '14px', whiteSpace: 'nowrap' }}>Instructions:</Typography>
        <TextField
          fullWidth
          multiline
          rows={2}
          value={localState.instructions}
          onChange={handleInputChange('instructions')}
          variant="outlined"
          sx={{ padding: 0 }}
        />
      </Box>

      {/* Purchase Activity */}
      <Box>
        <PurchaseActivity swarm={swarm} />
      </Box>

      {/* Actions row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
        <BuyManagerSelect 
          value={localState.buyManagerId || ''}
          onChange={handleInputChange('buyManagerId')}
        />

        <Button
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          onClick={handleSaveChanges}
          disabled={isEditing}
        >
          {isEditing ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>
    </Box>
  );
}
